// NotificationList.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import notificationService from "../../services/NotificationService";
import "./NotificationList.css";

const NotificationList = ({
  isOpen,
  onClose,
  maxHeight = "400px",
  showMarkAllAsRead = true,
  className = "",
}) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const listRef = useRef(null);

  const loadNotifications = useCallback(
    async (loadMore = false) => {
      try {
        setLoading(true);

        const token = loadMore ? nextToken : null;

        const response = await notificationService.loadNotifications(
          20,
          token,
          {}
        );

        if (loadMore) {
          setNotifications((prev) => [...prev, ...response.items]);
        } else {
          setNotifications(response.items || []);
        }

        setNextToken(response.nextToken);
        setHasMore(!!response.nextToken);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error("Failed to load notifications:", error);
      } finally {
        setLoading(false);
      }
    },
    [nextToken]
  );

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  useEffect(() => {
    // Listen for real-time updates
    const unsubscribeCreated = notificationService.addEventListener(
      "notificationCreated",
      (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setTotalCount((prev) => prev + 1);
      }
    );

    const unsubscribeUpdated = notificationService.addEventListener(
      "notificationUpdated",
      (updatedNotification) => {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === updatedNotification.id ? updatedNotification : n
          )
        );
      }
    );

    const unsubscribeDeleted = notificationService.addEventListener(
      "notificationDeleted",
      (deletedNotification) => {
        setNotifications((prev) =>
          prev.filter((n) => n.id !== deletedNotification.id)
        );
        setTotalCount((prev) => prev - 1);
      }
    );

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeDeleted();
    };
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setLoading(true);
      await notificationService.markAllAsRead();
      // Refresh the list
      await loadNotifications();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop === clientHeight && hasMore && !loading) {
      loadNotifications(true);
    }
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      info: "ℹ️",
      success: "✅",
      warning: "⚠️",
      error: "❌",
      offer_received: "📬",
      offer_accepted: "✨",
      offer_rejected: "❌",
      requirement_matched: "🎯",
      payment_due: "💳",
      system_update: "🔄",
    };
    return iconMap[type] || "📋";
  };

  const getNotificationColor = (type) => {
    const colorMap = {
      info: "#17a2b8",
      success: "#28a745",
      warning: "#ffc107",
      error: "#dc3545",
      offer_received: "#007bff",
      offer_accepted: "#28a745",
      offer_rejected: "#dc3545",
      requirement_matched: "#6f42c1",
      payment_due: "#fd7e14",
      system_update: "#6c757d",
    };
    return colorMap[type] || "#6c757d";
  };

  if (!isOpen) return null;

  return (
    <div className={`notification-list-overlay ${className}`} onClick={onClose}>
      <div
        className="notification-list-container"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight }}
      >
        {/* Header */}
        <div className="notification-list-header">
          <h3 className="notification-list-title">
            Notifications {totalCount > 0 && `(${totalCount})`}
          </h3>
          <div className="notification-list-actions">
            {showMarkAllAsRead && notifications.some((n) => !n.isRead) && (
              <button
                className="notification-mark-all-btn"
                onClick={handleMarkAllAsRead}
                disabled={loading}
              >
                Mark all read
              </button>
            )}
            <button className="notification-close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* Notification List */}
        <div
          className="notification-list-content"
          onScroll={handleScroll}
          ref={listRef}
        >
          {loading && notifications.length === 0 ? (
            <div className="notification-list-loading">
              <div className="notification-loading-spinner"></div>
              <span>Loading notifications...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-list-empty">
              <div className="notification-empty-icon">📭</div>
              <h4>No notifications</h4>
              <p>You're all caught up!</p>
            </div>
          ) : (
            <>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${
                    !notification.isRead ? "unread" : ""
                  }`}
                  onClick={() =>
                    !notification.isRead && handleMarkAsRead(notification.id)
                  }
                >
                  <div className="notification-item-content">
                    <div
                      className="notification-item-icon"
                      style={{ color: getNotificationColor(notification.type) }}
                    >
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-item-text">
                      <div className="notification-item-title">
                        {notification.title}
                      </div>
                      <div className="notification-item-message">
                        {notification.message}
                      </div>
                      <div className="notification-item-time">
                        {formatRelativeTime(notification.createdAt)}
                      </div>
                    </div>
                    {!notification.isRead && (
                      <div className="notification-item-badge"></div>
                    )}
                  </div>
                  <div className="notification-item-actions">
                    <button
                      className="notification-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotification(notification.id);
                      }}
                      title="Delete notification"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}

              {loading && hasMore && (
                <div className="notification-list-loading">
                  <div className="notification-loading-spinner"></div>
                  <span>Loading more...</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationList;
